export * from "ce/AppRouter";
import { Routes as CE_Routes } from "ce/AppRouter";
import React, { Suspense, useEffect } from "react";
import history from "utils/history";
import AppHeader from "ee/pages/common/AppHeader";
import { Router, Switch } from "react-router-dom";
import ErrorPage from "pages/common/ErrorPage";
import PageLoadingBar from "pages/common/PageLoadingBar";
import ErrorPageHeader from "pages/common/ErrorPageHeader";
import { useDispatch, useSelector } from "react-redux";

import useBrandingTheme from "utils/hooks/useBrandingTheme";
import RouteChangeListener from "RouteChangeListener";
import { isValidLicense } from "ee/selectors/organizationSelectors";
import LicenseCheckPage from "./pages/LicenseSetup/LicenseCheckPage";
import {
  BASE_URL,
  LICENSE_CHECK_PATH,
  MIGRATIONS_URL,
  ORG_UPDATE_PATH,
} from "constants/routes";
import { requiresLicenseCheck } from "./requiresLicenseCheck";
import ProductAlertBanner from "components/editorComponents/ProductAlertBanner";
import Walkthrough from "components/featureWalkthrough";
import PackageIDELoader from "./pages/PackageIDE";
import {
  MODULE_EDITOR_PATH,
  PACKAGE_EDITOR_PATH,
  UI_PACKAGE_CUSTOM_WIDGETS_EDITOR_ID_PATH,
  UI_PACKAGE_EDITOR_PATH,
  UI_PACKAGE_MODULE_EDITOR_PATH,
} from "ee/constants/routes/packageRoutes";
import {
  getShowQueryModule,
  getShowUIModule,
} from "ee/selectors/moduleFeatureSelectors";
import { Migrations } from "./pages/Billing/Migrations";
import { WORKFLOW_EDITOR_URL } from "./constants/routes/workflowRoutes";
import WorkflowIDELoader from "./pages/WorkflowIDE";
import { getShowWorkflowFeature } from "./selectors/workflowSelectors";
import { getIsConsolidatedPageLoading } from "selectors/ui";
import { initCurrentPage } from "actions/initActions";
import { getSafeCrash, getSafeCrashCode } from "selectors/errorSelectors";
import type { ERROR_CODES } from "ee/constants/ApiConstants";
import { SentryRoute } from "components/SentryRoute";
import CloudBilling from "./pages/CloudBilling";
import { useIsCloudBillingEnabled } from "hooks/useIsCloudBillingEnabled";
import UIPackageIDELoader from "./pages/UIPackageIDE";
import OrgUpdate from "./pages/CloudBillingWelcome/OrgUpdate";
import CustomWidgetBuilderLoader from "pages/Editor/CustomWidgetBuilder/loader";
import { checkNonLoginPageIsWithinOrg } from "./hoc/checkNonLoginPageIsWithinOrg";

const loadingIndicator = <PageLoadingBar />;

// Create a combined HOC that applies both checks
const withLicenseAndOrgCheck = (Component: React.ComponentType) => {
  // First apply the license check
  const WithLicenseCheck = requiresLicenseCheck(Component);

  // Then apply the organization check
  return checkNonLoginPageIsWithinOrg(WithLicenseCheck);
};

// Use the combined HOC
const EE_Routes = withLicenseAndOrgCheck(() => {
  return <CE_Routes />;
});

const UI_PACKAGE_ROUTES = [
  UI_PACKAGE_MODULE_EDITOR_PATH,
  UI_PACKAGE_EDITOR_PATH,
];

function AppRouter() {
  const isLicenseValid = useSelector(isValidLicense);
  const safeCrash: boolean = useSelector(getSafeCrash);
  const safeCrashCode: ERROR_CODES | undefined = useSelector(getSafeCrashCode);

  const showQueryModule = useSelector(getShowQueryModule);
  const showUIModule = useSelector(getShowUIModule);
  const showWorkflows = useSelector(getShowWorkflowFeature);
  const isConsolidatedPageLoading = useSelector(getIsConsolidatedPageLoading);
  const isCloudBillingEnabled = useIsCloudBillingEnabled();

  const hostname = window.location.hostname;
  const isLoginHostname = hostname.split(".")[0] === "login";

  const dispatch = useDispatch();

  useEffect(
    function initializeCurrentPage() {
      dispatch(initCurrentPage());
    },
    [dispatch],
  );

  useBrandingTheme();
  const isLoading = isConsolidatedPageLoading;

  useEffect(
    function hideTopLoaderOnceOrganizationLoaded() {
      if (!isLoading) {
        const loader = document.getElementById("loader") as HTMLDivElement;

        if (loader) {
          loader.style.width = "100vw";

          setTimeout(() => {
            loader.style.opacity = "0";
          });
        }
      }
    },
    [isLoading],
  );

  if (isLoading) return null;

  if (isCloudBillingEnabled && isLoginHostname) {
    return (
      <Router history={history}>
        <Suspense fallback={loadingIndicator}>
          <RouteChangeListener />
          <SentryRoute component={CloudBilling} path={BASE_URL} />
        </Suspense>
      </Router>
    );
  }

  return (
    <Router history={history}>
      <Suspense fallback={loadingIndicator}>
        {safeCrash && safeCrashCode ? (
          <>
            <ErrorPageHeader />
            <ErrorPage code={safeCrashCode} />
          </>
        ) : (
          <>
            <RouteChangeListener />
            <Walkthrough>
              {isLicenseValid && <AppHeader />}
              <Switch>
                {!isLicenseValid && (
                  <SentryRoute
                    component={LicenseCheckPage}
                    path={LICENSE_CHECK_PATH}
                  />
                )}

                {showWorkflows && (
                  <SentryRoute
                    component={WorkflowIDELoader}
                    path={WORKFLOW_EDITOR_URL}
                  />
                )}

                {showQueryModule && (
                  <SentryRoute
                    component={PackageIDELoader}
                    path={MODULE_EDITOR_PATH}
                  />
                )}

                {showQueryModule && (
                  <SentryRoute
                    component={PackageIDELoader}
                    path={PACKAGE_EDITOR_PATH}
                  />
                )}
                {showUIModule && (
                  <SentryRoute
                    component={CustomWidgetBuilderLoader}
                    exact
                    path={UI_PACKAGE_CUSTOM_WIDGETS_EDITOR_ID_PATH}
                  />
                )}
                {showUIModule && (
                  <SentryRoute
                    component={UIPackageIDELoader}
                    path={UI_PACKAGE_ROUTES}
                  />
                )}
                <SentryRoute component={Migrations} path={MIGRATIONS_URL} />
                <SentryRoute component={OrgUpdate} path={ORG_UPDATE_PATH} />

                <EE_Routes />
              </Switch>
            </Walkthrough>
            <ProductAlertBanner />
          </>
        )}
      </Suspense>
    </Router>
  );
}

export default AppRouter;
